import { useEffect, useLayoutEffect, useRef } from 'react';
import { ContentParser } from '../../components/ContentParser';
import { CloseIcon } from '../../icons/CloseIcon';
import { Bar, Content, Dismiss, Heading } from './AnnouncementBanner.styles';
import type { AnnouncementBannerProps } from './AnnouncementBanner.types';

const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export function AnnouncementBanner({
  heading,
  body,
  dismissible = false,
  onDismiss,
  closeLabel = 'Dismiss',
}: AnnouncementBannerProps) {
  const barRef = useRef<HTMLDivElement>(null);

  useIsomorphicLayoutEffect(() => {
    const el = barRef.current;
    if (!el) return undefined;

    const root = document.documentElement;
    const write = (height: number) => {
      root.style.setProperty('--announcement-banner-height', `${height}px`);
    };

    write(el.getBoundingClientRect().height);

    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
        write(height);
      });
      observer.observe(el);
    }

    return () => {
      observer?.disconnect();
      root.style.removeProperty('--announcement-banner-height');
    };
  }, []);

  return (
    <Bar ref={barRef} role="region" aria-label={heading || 'Announcement'}>
      <Content>
        {heading && <Heading>{heading}</Heading>}
        <ContentParser text={body} />
      </Content>
      {dismissible && onDismiss && (
        <Dismiss type="button" onClick={onDismiss} aria-label={closeLabel}>
          <CloseIcon size={18} />
        </Dismiss>
      )}
    </Bar>
  );
}
